import { useEffect, useState } from 'react';

import type { UiMilestone, UiPhase } from '@nightshift/ui-types';

import { api } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import type { ConnectionState } from '../live/store.ts';
import { milestoneChoices, orderPhases, phaseProgress, type MilestoneChoiceGroup } from '../lib/phase-filter.ts';
import { phaseName } from '../lib/taxonomy.ts';
import { MuteIcon, PlusIcon, SpeakerIcon } from './icons.tsx';
import { NightManagerButton } from '../components/night-manager/popover.tsx';
import { NotificationCentre } from './notifications.tsx';
import { ProgressPicker } from './progress-picker.tsx';

export function Topbar({
  sound,
  showMilestone = true,
  onSound,
  onSelectProject,
  onSelectPhase,
  onSelectMilestone,
  onCreateTicket,
  onOpenTicket,
  onOpenView,
}: {
  sound: boolean;
  showMilestone?: boolean;
  onSound: (next: boolean) => void;
  onSelectProject: (slug: string | null) => void;
  onSelectPhase: (id: number | null) => void;
  onSelectMilestone: (id: number | null) => void;
  onCreateTicket: () => void;
  onOpenTicket: (key: string, run?: number, project?: string | null) => void;
  /** Where an operator alert's notification goes: the Agents view or Ops. */
  onOpenView: (view: 'agents' | 'ops') => void;
}) {
  const state = useLive();
  const snapshot = state.snapshot;
  const project = snapshot?.projects.find((item) => item.slug === state.projectSlug);

  return (
    <header className="topbar type-fixed">
      <div className="topbar-brand">
        <span className="nav-mark" aria-hidden>
          N
        </span>
        <div className="topbar-brand-copy">
          <span className="topbar-product">Nightshift</span>
          <span className="topbar-subtitle">Development Control Center</span>
        </div>
      </div>

      <button
        type="button"
        className="btn btn-accent btn-sm"
        aria-label="Create Ticket"
        onClick={onCreateTicket}
      >
        <PlusIcon size={12} />
        Create Ticket
      </button>

      <ScopePicker
        onSelectProject={onSelectProject}
        currentSlug={state.projectSlug}
        projectName={project?.name ?? null}
        projectKey={project?.key ?? null}
        phase={project ? phaseName(project.phase) : null}
      />

      {showMilestone && state.projectSlug ? <PhasePicker
        phases={state.phases}
        milestones={state.milestones.filter((item) => item.projectSlug === state.projectSlug)}
        selectedId={state.phaseId}
        onSelect={onSelectPhase}
      /> : null}

      {showMilestone ? <MilestonePicker
        // Across every project there is no single plan to order by, so that list stays as it comes.
        groups={state.projectSlug
          ? milestoneChoices(state.milestones, state.phases, state.phaseId)
          : [{ phase: null, milestones: state.milestones }]}
        grouped={state.projectSlug !== null && state.phaseId === null}
        selectedId={state.milestoneId}
        onSelect={onSelectMilestone}
      /> : null}

      <span className="topbar-spacer" />

      <LivePill connection={state.connection} />

      <ConfigHealthIndicator projectSlug={state.projectSlug} />

      <NightManagerButton onOpenTicket={onOpenTicket} />

      <NotificationCentre onOpenTicket={onOpenTicket} onOpenView={onOpenView} />

      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => onSound(!sound)}
        title={
          sound
            ? 'A chime plays when something starts waiting on you. Click to mute.'
            : 'Muted. Click to play a chime when something starts waiting on you.'
        }
        aria-pressed={sound}
        aria-label={sound ? 'Mute chime' : 'Unmute chime'}
      >
        {sound ? <SpeakerIcon size={14} /> : <MuteIcon size={14} />}
      </button>
    </header>
  );
}

function ConfigHealthIndicator({ projectSlug }: { projectSlug: string | null }) {
  const [issues, setIssues] = useState<Array<{ severity: 'blocking' | 'warning'; message: string; project: string }>>([]);
  useEffect(() => {
    let active = true;
    const read = () => api.config(projectSlug).then((result) => {
      if (active) setIssues(result.configHealth);
    }).catch(() => {
      // The Live pill already communicates a transport outage; do not turn it into a second alert.
    });
    void read();
    const timer = window.setInterval(read, 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [projectSlug]);
  const blocking = issues.filter((issue) => issue.severity === 'blocking').length;
  const label = blocking > 0 ? `${blocking} configuration issue${blocking === 1 ? '' : 's'}` : issues.length > 0 ? `${issues.length} configuration warning${issues.length === 1 ? '' : 's'}` : 'Configuration healthy';
  return (
    <a
      className="btn btn-ghost btn-sm"
      href={projectSlug ? `/p/${encodeURIComponent(projectSlug)}/settings?group=agents` : '/settings?group=agents'}
      title={issues.length > 0 ? issues.map((issue) => `${issue.project}: ${issue.message}`).join(' · ') : label}
      aria-label={label}
      style={{ color: blocking > 0 ? 'var(--danger, #d06b42)' : issues.length > 0 ? 'var(--amber, #d6a84f)' : undefined }}
    >
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: blocking > 0 ? 'currentColor' : issues.length > 0 ? 'currentColor' : 'var(--good, #6f9b75)' }} />
      {blocking > 0 ? 'Config issue' : issues.length > 0 ? 'Config warning' : 'Config OK'}
    </a>
  );
}

function ScopePicker({
  onSelectProject,
  currentSlug,
  projectName,
  projectKey,
  phase,
}: {
  onSelectProject: (slug: string | null) => void;
  currentSlug: string | null;
  projectName: string | null;
  projectKey: string | null;
  phase: string | null;
}) {
  const state = useLive();
  const snapshot = state.snapshot;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="picker"
        aria-label="Scope"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="topbar-filter-label">Scope</span>
        {projectName ?? currentSlug ?? 'All projects'}
        {projectKey ? (
          <span className="mono dim" style={{ fontSize: 'var(--fs-tiny)' }}>
            {projectKey}
          </span>
        ) : null}
        {phase ? (
          <span className="dim picker-phase" style={{ fontSize: 'var(--fs-micro)' }}>
            {phase}
          </span>
        ) : null}
        <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
          ▾
        </span>
      </button>
      {menuOpen ? (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 149 }}
            onClick={() => setMenuOpen(false)}
            role="presentation"
          />
          <div className="menu" role="menu" aria-label="Projects" style={{ top: 'calc(100% + 6px)', left: 0 }}>
            <button
              type="button"
              className="menu-item"
              role="menuitem"
              aria-current={currentSlug === null}
              onClick={() => {
                onSelectProject(null);
                setMenuOpen(false);
              }}
            >
              All projects
              <span className="dim num" style={{ marginLeft: 'auto', fontSize: 'var(--fs-tiny)' }}>
                {snapshot?.projects.length ?? 0}
              </span>
            </button>
            {(snapshot?.projects ?? []).map((item) => (
              <button
                key={item.slug}
                type="button"
                className="menu-item"
                role="menuitem"
                aria-current={currentSlug === item.slug}
                onClick={() => {
                  onSelectProject(item.slug);
                  setMenuOpen(false);
                }}
              >
                {item.name}
                <span className="mono dim" style={{ fontSize: 'var(--fs-tiny)' }}>
                  {item.key}
                </span>
                <span className="dim num" style={{ marginLeft: 'auto', fontSize: 'var(--fs-tiny)' }}>
                  {item.openCount} open
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Narrows Board, Tickets and the Milestone choices to one phase. Phases list in their planned order. */
function PhasePicker({
  phases,
  milestones,
  selectedId,
  onSelect,
}: {
  phases: UiPhase[];
  milestones: UiMilestone[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  return <ProgressPicker label="Phase" selectedId={selectedId} onSelect={onSelect}
    groups={[{ label: null, options: orderPhases(phases).map((phase) => ({
      id: phase.id, name: phase.name, ...phaseProgress(phase, milestones),
    })) }]} />;
}

/**
 * With no phase chosen the milestones are grouped under their phases; with one chosen the list
 * holds that phase's milestones alone. Either way they follow their planned order, not their names.
 */
function MilestonePicker({
  groups,
  grouped,
  selectedId,
  onSelect,
}: {
  groups: MilestoneChoiceGroup[];
  grouped: boolean;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  // A project without phases does not need a group heading.
  const headed = grouped && groups.some((group) => group.phase !== null);
  return <ProgressPicker label="Milestone" selectedId={selectedId} onSelect={onSelect}
    groups={groups.map((group) => ({
      label: headed ? group.phase ?? 'No phase' : null,
      options: group.milestones.map((item) => ({ id: item.id, name: item.name, completed: item.doneCount, total: item.ticketCount })),
    }))} />;
}

function LivePill({ connection }: { connection: ConnectionState }) {
  const store = useStore();
  const live = connection === 'live';
  return (
    <button
      type="button"
      className={`live-pill${live ? ' is-live' : ''}`}
      title={
        live
          ? 'Receiving live events'
          : connection === 'connecting'
            ? 'Connecting to the event stream'
            : 'Event stream is not live. Click to retry.'
      }
      onClick={() => {
        if (!live) store.reconnectNow();
      }}
    >
      <span className={`lamp${live ? ' lamp-breathe' : ''}`} aria-hidden />
      {live ? 'Live' : connection === 'connecting' ? 'Connecting' : connection === 'stale' ? 'Quiet' : 'Offline'}
    </button>
  );
}
